import assert from 'assert-ts';
import type {JSONValue} from 'types';
import type {GetTokens} from 'api/types';
import {Configuration} from 'configuration';
import type {
  ElasticCloudAuthLambda,
  Highlighter,
  SearchCountResultDto,
  SearchType,
} from 'api/searchTypes';
import {HighlightPostTag, HighlightPreTag} from 'api/searchTypes';
import {arrayToJsonValue} from 'services/utils';
import {getOffset} from 'services/utils/getOffset';
import {httpPost} from './http';

export const httpElasticCount = (
  getTokens: GetTokens,
  index: string,
  query: JSONValue[],
  abortSignal?: AbortSignal,
  mock?: boolean,
): Promise<SearchCountResultDto> => {
  const elasticBody: JSONValue[] = [];

  query.forEach(q => {
    elasticBody.push({
      size: 0, // Only need the counts/totals, not the actual hits.
      query: q,
    });
  });

  const body: ElasticCloudAuthLambda = {
    target: '_msearch',
    index,
    elasticBody,
  };

  return httpPost<SearchCountResultDto, ElasticCloudAuthLambda>({
    subdir: '',
    body,
    getTokens,
    abortSignal,
    mock,
  });
};

export const httpElasticV2 = <T>(
  getTokens: GetTokens,
  index: string,
  query: JSONValue,
  page = 0,
  size: number,
  highlighter?: Highlighter,
  abortSignal?: AbortSignal,
  mock?: boolean,
  sort?: JSONValue,
): Promise<T> => {
  const from = getOffset(page, size);

  const body: ElasticCloudAuthLambda = {
    target: '_search',
    index,
    elasticBody: {
      size,
      from,
      query,
      sort: sort ? sort : [],
      highlight: highlighter
        ? {
            pre_tags: HighlightPreTag,
            post_tags: HighlightPostTag,
            fields: arrayToJsonValue(highlighter.fields),
            // We apply a separate query for highlights to evade fuzzy matches.
            highlight_query: {
              bool: {
                should: {
                  multi_match: {
                    fields: highlighter.fields,
                    operator: 'and',
                    query: highlighter.query,
                    type: 'cross_fields',
                  },
                },
              },
            },
          }
        : {},
    },
  };

  return httpPost<T, ElasticCloudAuthLambda>({
    subdir: '',
    body,
    getTokens,
    abortSignal,
    mock,
  });
};

/**
 * @deprecated
 */
export const httpElastic = <T>(
  getTokens: GetTokens,
  target: ElasticCloudAuthLambda['target'],
  searchType: SearchType,
  query: JSONValue,
  page = 0,
  size: number,
  minScore = 0.1,
  abortSignal?: AbortSignal,
  mock?: boolean,
): Promise<T> => {
  const {indexes} = Configuration.searchApi.elastic;
  const searchTypeIndex = indexes[searchType];

  assert(
    searchTypeIndex,
    `Expected index on search type ${searchType}: Might be missing a configuration key for index/environment`,
    {indexes},
  );

  const from = getOffset(page, size);

  const body: ElasticCloudAuthLambda = {
    target: target,
    index: searchTypeIndex,
    elasticBody: {
      size,
      from,
      query,
      min_score: minScore,
    },
  };

  return httpPost<T, ElasticCloudAuthLambda>({
    subdir: '',
    body,
    getTokens,
    abortSignal,
    mock,
  });
};
